import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { Field, type FieldProps, Formik } from "formik";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import type { TableIdEnum } from "./table-base";

export type OptionType = {
	/** The value of the option. Used in combination with the query param. */
	value: string;

	/* Text to be displayed as the label */
	label: string;
};

export type AdditionalFilterType = {
	/** The key of the filter, will also be used as the query parameter */
	key: string;

	/** The label of the additional filter */
	label: string;

	/** Make a filter hidden */
	hidden?: boolean;

	/** List with options. Only used with the `select` filter */
	option_list: OptionType[];

	/* The type of filter that is shown */
	type: "multi-select" | "select" | "switch";
};
export type AdditionalFilterParams = {
	[key: string]: string | number | boolean | undefined | OptionType[];
};

export type TableBaseFilterRef = {
	setFieldValue: (field: string, value: any) => void;
	resetForm: () => void;
};

type TableBaseFilterProps = {
	/**
	 * Define additional filters which expand the capabilities to which the table content can be filtered. Additional filters appear on the top right of the table.
	 *
	 * Additional filters can be used to filter the table content on properties that don't appear in a column (e.g. if a user is disabled or not, if a user is a private or business debtor, etc.)
	 *
	 * ### Example: Show disabled users too using a switch and filter between private and business users using a dropdown select
	 * ```javascript
	 *	<TableBase
		...
		additional_filters={[
			{
				key: 'show_disabled',
				type: 'switch',
				label: t('back_office.customers.user_management.show_disabled')
			},
			{
				key: 'membership_type',
				type: 'select',
				label: t('back_office.customers.user_management.membership_type.label'),
				option_list: [
					{
						value: 'private',
						label: t('back_office.customers.user_management.membership_type.private')
					},
					{
						value: 'business',
						label: t('back_office.customers.user_management.membership_type.business')
					}
				]
			}
		]}
		...
	/>
	 * ```
	 */
	additional_filters?: AdditionalFilterType[];

	/** Optional flag to determ the position of the additional filters */
	additional_filters_own_line?: boolean;

	/**
	 * Show the reset button. The reset button will reset the sorting, offset and search query to its default state when clicked.
	 *
	 * Custom logic can be assigned to the reset button by using the `on_reset_button_handler` prop.
	 */
	enable_reset_button: boolean;
	/**
	 * Show the search field. The search field already has logic to handle search queries, so no setup in the front-end is required.
	 *
	 * Custom logic can be assigned to the search field by using the `on_search_handler` prop.
	 */
	enable_search: boolean;
	loading: boolean;
	on_additional_filter_handler: (paramsAdditional: AdditionalFilterParams) => void;
	on_search_handler: (query: string) => void;
	on_reset_button_handler: () => void;
	/** The search query to find items in the table */
	query: string;
	table_id: TableIdEnum;
};

/* -------------------------------------------------------------------------- */
/*                              TABLE BASE FILTER                             */
/* -------------------------------------------------------------------------- */
const TableBaseFilter = forwardRef<TableBaseFilterRef, TableBaseFilterProps>(
	(
		{
			additional_filters_own_line = false,
			additional_filters = [],
			enable_reset_button,
			enable_search,
			loading,
			on_additional_filter_handler,
			on_search_handler,
			on_reset_button_handler,
			query,
			table_id,
		},
		ref,
	) => {
		const { t } = useTranslation();

		const sanitizeParamsAdditional = (paramsAdditional: AdditionalFilterParams) => {
			const paramsAdditionalSanitized = {};
			const multiselectKeys = additional_filters
				.filter((v) => v.type === "multi-select")
				.map((v) => v.key);

			for (const [key, paramAdditional] of Object.entries(paramsAdditional)) {
				if (!multiselectKeys.includes(key)) {
					paramsAdditionalSanitized[key] = paramAdditional;
					continue;
				}
				paramsAdditionalSanitized[key] = paramAdditional.map((param) => param.value).join(",");
			}
			on_additional_filter_handler(paramsAdditionalSanitized);
		};

		const determFilterWidthDesktop = () => {
			if (additional_filters_own_line) {
				return 12;
			}

			if (additional_filters.length === 0) {
				return 12;
			}

			return 6;
		};

		return (
			<Grid container sx={{ mb: 2 }} rowSpacing={2.5} display="flex" alignItems="center">
				<Grid item xs={12} md={determFilterWidthDesktop()}>
					{/* Enable search */}
					{enable_search && (
						<TextField
							variant="outlined"
							sx={{}}
							size="small"
							placeholder={t("table_filter.toolbar.search")}
							type="search"
							onChange={(event) => on_search_handler(event.target.value)}
							value={query}
						/>
					)}

					{/* Enable reset button  */}
					{enable_reset_button && (
						<Chip
							disabled={loading}
							label={t("billing.orders.table.reset")}
							icon={<AutorenewIcon />}
							color="info"
							variant="outlined"
							sx={{ ml: enable_search ? 2 : 0, my: 0.5 }}
							onClick={on_reset_button_handler}
						/>
					)}
				</Grid>
				{/* Additional filters */}
				<AdditionalFilterSection
					ref={ref}
					additional_filters_own_line={additional_filters_own_line}
					sanitize_params_additional={sanitizeParamsAdditional}
					additional_filters={additional_filters}
					table_id={table_id}
				/>
			</Grid>
		);
	},
);

/* -------------------------------------------------------------------------- */
/*                          ADDITIONAL FILTER SECTION                         */
/* -------------------------------------------------------------------------- */
type AdditionalFilterSectionProps = {
	additional_filters_own_line?: boolean;
	sanitize_params_additional: (paramsAdditional: AdditionalFilterParams) => void;
	additional_filters?: AdditionalFilterType[];
	table_id: TableIdEnum;
};

const AdditionalFilterSection = forwardRef<TableBaseFilterRef, AdditionalFilterSectionProps>(
	(
		{
			additional_filters_own_line = false,
			additional_filters,
			sanitize_params_additional,
			table_id,
		},
		ref,
	) => {
		const determInitialValues = () => {
			const initialValues = {};

			for (const additionalFilter of additional_filters) {
				switch (additionalFilter.type) {
					case "multi-select":
						initialValues[additionalFilter.key] = [];
						break;
					case "select":
						initialValues[additionalFilter.key] = "";
						break;
					case "switch":
						initialValues[additionalFilter.key] = false;
						break;
				}
			}
			return initialValues;
		};

		// Return nothing when empty
		if (additional_filters.length === 0) {
			return null;
		}

		return (
			<Formik
				key={table_id}
				initialValues={determInitialValues()}
				onSubmit={() => {}}
				innerRef={(instance) => {
					if (ref && typeof ref === "object") {
						ref.current = {
							resetForm: () => {
								instance?.resetForm();
							},
							setFieldValue: (field: string, value: any) => {
								instance?.setFieldValue(field, value);
							},
						};
					}
				}}
			>
				<Grid item xs={12} md={additional_filters_own_line ? 12 : 6}>
					{additional_filters_own_line && <Divider />}
					<Box
						display={{ md: "flex" }}
						alignItems="center"
						justifyContent={{ md: additional_filters_own_line ? "flex-start" : "flex-end" }}
						sx={{ gap: 2 }}
					>
						{additional_filters.map((additionalFilter) => (
							<AdditionalFilterItem
								key={additionalFilter.key}
								additional_filter={additionalFilter}
								sanitize_params_additional={sanitize_params_additional}
							/>
						))}
					</Box>
				</Grid>
			</Formik>
		);
	},
);

/* -------------------------------------------------------------------------- */
/*                       ADDITIONAL FILTER ITEM SECTION                       */
/* -------------------------------------------------------------------------- */
type AdditionalFilterItemProps = {
	additional_filter: AdditionalFilterType;
	sanitize_params_additional: (paramsAdditional: AdditionalFilterParams) => void;
};

function AdditionalFilterItem({
	additional_filter,
	sanitize_params_additional,
}: AdditionalFilterItemProps): JSX.Element {
	if (additional_filter?.hidden === true) {
		return;
	}

	switch (additional_filter.type) {
		case "multi-select": {
			return (
				<AdditionalFilterMultiSelect
					additional_filter={additional_filter}
					sanitize_params_additional={sanitize_params_additional}
				/>
			);
		}

		case "select": {
			return (
				<AdditionalFilterItemSelect
					additional_filter={additional_filter}
					sanitize_params_additional={sanitize_params_additional}
				/>
			);
		}

		case "switch": {
			return (
				<AdditionalFilterItemSwitch
					additional_filter={additional_filter}
					sanitize_params_additional={sanitize_params_additional}
				/>
			);
		}

		default:
			return <Box component="span">{`Type '${additional_filter.type}' not implemented.`}</Box>;
	}
}

/* -------------------------------------------------------------------------- */
/*                             MULTI-SELECT FILTER                            */
/* -------------------------------------------------------------------------- */
function AdditionalFilterMultiSelect({
	additional_filter,
	sanitize_params_additional,
}: AdditionalFilterItemProps) {
	return (
		<Field name={additional_filter.key}>
			{({ field, form }: FieldProps) => (
				<Autocomplete
					size="small"
					fullWidth
					{...field}
					options={additional_filter.option_list}
					isOptionEqualToValue={(option, value) => {
						if (!(option && value)) {
							return false;
						}

						return option?.value === value?.value;
					}}
					multiple
					value={field.value}
					renderInput={(params) => (
						<TextField
							{...params}
							label={additional_filter.label}
							margin="normal"
							variant="outlined"
						/>
					)}
					onChange={(_, value) => {
						form.setFieldValue(additional_filter.key, value);

						sanitize_params_additional({
							...form.values,
							[additional_filter.key]: value,
						});
					}}
				/>
			)}
		</Field>
	);
}

/* -------------------------------------------------------------------------- */
/*                                SELECT FILTER                               */
/* -------------------------------------------------------------------------- */
function AdditionalFilterItemSelect({
	additional_filter,
	sanitize_params_additional,
}: AdditionalFilterItemProps) {
	return (
		<Field name={additional_filter.key}>
			{({ field, form }: FieldProps) => (
				<FormControl size="small" fullWidth>
					<InputLabel>{additional_filter.label}</InputLabel>
					<Select
						{...field}
						label={additional_filter.label}
						endAdornment={
							field.value ? (
								// Adds an X button to the end of the select field to remove the current filter
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => {
											form.setFieldValue(additional_filter.key, "");
											sanitize_params_additional({
												...form.values,
												[additional_filter.key]: "",
											});
										}}
										sx={{ mr: 1 }}
									>
										<CloseIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : null
						}
						onChange={(event) => {
							field.onChange(event);
							sanitize_params_additional({
								...form.values,
								[additional_filter.key]: event.target.value,
							});
						}}
					>
						{additional_filter.option_list.map((option) => (
							<MenuItem id={option.value} key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}
		</Field>
	);
}

/* -------------------------------------------------------------------------- */
/*                                SWITCH FILTER                               */
/* -------------------------------------------------------------------------- */
function AdditionalFilterItemSwitch({
	additional_filter,
	sanitize_params_additional,
}: AdditionalFilterItemProps) {
	return (
		<Field name={additional_filter.key}>
			{({ field, form }: FieldProps) => (
				<FormGroup>
					<FormControlLabel
						labelPlacement="start"
						sx={{ mr: -0.7 }}
						control={
							<Switch
								size="small"
								sx={{ ml: 1 }}
								checked={field.value === true}
								onChange={(_, checked) => {
									form.setFieldValue(additional_filter.key, checked);
									sanitize_params_additional({
										...form.values,
										[additional_filter.key]: checked,
									});
								}}
							/>
						}
						label={
							<Box component="span" fontSize={14}>
								{additional_filter.label}
							</Box>
						}
					/>
				</FormGroup>
			)}
		</Field>
	);
}

// Display names for debugging
TableBaseFilter.displayName = "TableBaseFilter";
AdditionalFilterSection.displayName = "AdditionalFilterSection";

export default TableBaseFilter;
